import React, { useEffect } from 'react';
import { BrowserRouter as Router, Switch, Route } from 'react-router-dom';

import './App.css';

import Documents from './containers/Documents';
import Document from './containers/Document';
import About from './containers/About';
import Contact from './containers/Contact';
import Acronym from './containers/Acronym';
import UploadXML from './components/UploadXML';
import UploadText from './components/UploadText';
import Amplify, { Auth } from 'aws-amplify';
import { AuthState, onAuthUIStateChange, CognitoUserInterface } from '@aws-amplify/ui-components';
import { AmplifySignIn, AmplifyAuthenticator } from '@aws-amplify/ui-react';
import Header from './components/Header';
import Footer from './components/Footer';
import { setUserData } from './redux/actions';

import Dashboard from './containers/Dashboard';
import { useAppDispatch } from './redux/hooks';
import ErrorModal from './components/ui/ErrorModal/ErrorModal';
import Replacements from './containers/Replacements';
import Jobs from './containers/Jobs';
import JobForm from './containers/JobForm';
import Podcasts from './containers/Podcasts';
import Podcast from './containers/Podcast';
import PodcastSort from './containers/PodcastSort';
import Groups from './containers/Groups';
import GroupForm from './containers/GroupForm';
import Users from './containers/Users';
import UserForm from './containers/UserForm';

const config = {
  Auth: {
    region: process.env.REACT_APP_REGION,
    userPoolId: process.env.REACT_APP_USER_POOL_ID,
    userPoolWebClientId: process.env.REACT_APP_USER_POOL_WEB_CLIENT_ID,
    mandatorySignIn: true,
    oauth: {
      domain: process.env.REACT_APP_OAUTH_DOMAIN,
      scopes: [
        'phone',
        'email',
        'profile',
        'openid',
        'aws.cognito.signin.user.admin',
        process.env.REACT_APP_OAUTH_SCOPE_READ,
        process.env.REACT_APP_OAUTH_SCOPE_WRITE,
      ],
      redirectSignIn: process.env.REACT_APP_SIGNIN_URL,
      redirectSignOut: process.env.REACT_APP_SIGNOUT_URL,
      responseType: 'code',
    },
  },
  API: {
    endpoints: [
      {
        name: 'Columbia',
        endpoint: process.env.REACT_APP_ENDPOINT,
        custom_header: async () => {
          return {
            Authorization: `Bearer ${(await Auth.currentSession()).getAccessToken().getJwtToken()}`,
          };
        },
      },
    ],
  },
};

Amplify.configure(config);

const AuthStateApp = (): JSX.Element => {
  const [authState, setAuthState] = React.useState<AuthState>();
  const [user, setUser] = React.useState<CognitoUserInterface | undefined>();

  const dispatch = useAppDispatch();

  useEffect(() => {
    onAuthUIStateChange((nextAuthState, authData) => {
      const data = authData as CognitoUserInterface;
      setAuthState(nextAuthState);
      setUser(authData as CognitoUserInterface);
      if (data) dispatch(setUserData(data['attributes']));
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authState]);

  const signOut = async () => {
    try {
      await Auth.signOut();
      setUser(undefined);
      localStorage.clear();
      window.location.reload();
    } catch (error) {
      // console.log(error);
    }
  };

  return authState === AuthState.SignedIn && user ? (
    <Router>
      <Header signOut={signOut} user={user} />
      <div className="container-fluid">
        <Switch>
          <Route path="/document/:id" component={Document} />
          <Route path="/job/:id" component={JobForm} />
          <Route path="/podcast/:id" component={Podcast} />
          <Route path="/episodes/:id" component={PodcastSort} />
          <Route path="/new" component={UploadXML} />
          <Route path="/create" component={UploadText} />
          <Route path="/about" component={About} />
          <Route path="/contact" component={Contact} />
          <Route path="/acronym" component={Acronym} />
          <Route path="/documents" component={Documents} />
          <Route path="/replacements" component={Replacements} />
          <Route path="/jobs" component={Jobs} />
          <Route path="/podcasts" component={Podcasts} />
          <Route path="/group/:id" component={GroupForm} />
          <Route path="/groups" component={Groups} />
          <Route path="/user/:id" component={UserForm} />
          <Route path="/users" component={Users} />
          <Route path="/dashboard" component={Dashboard} />
          <Route path="/" component={Documents} />
        </Switch>
      </div>
      <ErrorModal />
      <Footer />
    </Router>
  ) : (
    <AmplifyAuthenticator>
      <AmplifySignIn hideSignUp={true} slot="sign-in"></AmplifySignIn>
    </AmplifyAuthenticator>
  );
};

export default AuthStateApp;
